/**
 * App-wide logging. A thin seam over the `log` package
 * (https://www.npmjs.com/package/log) plus the browser writer that
 * forwards enabled log events to the devtools console — the ONE place
 * in the app allowed to call `console.*` (PROD-8397).
 *
 * Usage:
 *
 * ```ts
 * import { getLogger } from '@/log';
 * const log = getLogger('scenes');       // namespaces are 'a-z0-9-' (+ ':' nesting)
 * log.error('poll failed', sceneId, err);  // → console.error('[scenes]', 'poll failed', …)
 * ```
 *
 * Levels: `error` > `warning` > `notice` > `info` > `debug`.
 *
 * Visibility: dev builds print every level; production builds print
 * `notice` and above. Runtime override (reload to apply):
 *
 * ```js
 * localStorage.LOG_LEVEL = 'debug';          // threshold for all namespaces
 * localStorage.LOG_DEBUG = 'scenes,-scenes:poll'; // expose namespaces below the threshold
 * ```
 */
import log from 'log';
import emitter from 'log/lib/emitter';
import setupVisibility from 'log/lib/setup-visibility';

/** Public logger shape (level methods + `.get()` for sub-namespaces). */
export type AppLogger = typeof log;

/** Returns the logger for a namespace. Namespaces must be kebab-case. */
export function getLogger(namespace: string): AppLogger {
    return log.get(namespace);
}

const CONSOLE_METHOD = {
    debug: 'debug',
    info: 'info',
    notice: 'info',
    warning: 'warn',
    error: 'error',
} as const;

function readSetting(key: string): string | null {
    // localStorage can throw in privacy modes; logging must never take
    // the app down over that.
    if (typeof window === 'undefined') return null;
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
}

// `import.meta.env` only exists where Vite (or Vitest) provides it —
// web-test-runner serves this file without it, and there we want the
// dev behavior (everything prints).
const IS_PROD_BUILD = Boolean(import.meta.env && import.meta.env.PROD);

// Survives module re-evaluation (Vite HMR, per-test-file module graphs)
// while the `log` package's emitter — external, cached — keeps its
// listeners: without the guard each re-eval would add a duplicate
// writer and every line would print twice.
const WRITER_INSTALLED = Symbol.for('mxt.log-console-writer');

function installConsoleWriter(): void {
    const holder = globalThis as Record<symbol, unknown>;
    if (holder[WRITER_INSTALLED]) return;
    holder[WRITER_INSTALLED] = true;

    setupVisibility(readSetting('LOG_LEVEL') ?? (IS_PROD_BUILD ? 'notice' : 'debug'), (readSetting('LOG_DEBUG') ?? '').split(','));

    emitter.on('log', (event) => {
        if (!event.logger.isEnabled) return;
        // Pass tokens through untouched (no printf pre-formatting) so
        // objects and errors stay live-inspectable in devtools. Looked
        // up per call, not pre-bound, so test spies on console still
        // observe writes.
        const args = event.logger.namespace ? [`[${event.logger.namespace}]`, ...event.messageTokens] : event.messageTokens;
        // eslint-disable-next-line no-console
        console[CONSOLE_METHOD[event.logger.level]](...args);
    });
}

installConsoleWriter();
